/**
 * usePermissions Hook
 * 
 * Provides role-based permission checking for React components.
 * Use this hook to conditionally render UI elements based on user permissions.
 * 
 * Role Reference:
 * - RoleID 1: Admin - Full system access (read, write, delete, admin)
 * - RoleID 2: Manager - CRUD operations (read, write, delete)
 * - RoleID 3: User - Standard access (read, write)
 * - RoleID 4: Viewer - Read-only access (read)
 * 
 * Usage:
 * ```jsx
 * import { usePermissions } from '../hooks/usePermissions';
 * 
 * function MyComponent() {
 *   const { canWrite, canDelete, isReadOnly } = usePermissions();
 *   
 *   return (
 *     <div>
 *       {canWrite && <button>Edit</button>}
 *       {canDelete && <button>Delete</button>}
 *       {isReadOnly && <span>View Only Mode</span>}
 *     </div>
 *   );
 * }
 * ```
 */

import { useAuth } from '../components/auth/AuthProvider';

// Permission definitions per role (mirrors server-side)
const rolePermissions = {
  1: ['read', 'write', 'delete', 'admin'],  // Admin
  2: ['read', 'write', 'delete'],           // Manager
  3: ['read', 'write'],                     // User
  4: ['read'],                              // Viewer
};

// Role names for display
const roleNames = {
  1: 'Admin',
  2: 'Manager',
  3: 'User',
  4: 'Viewer',
};

/**
 * Hook to check user permissions based on RoleID
 * @returns {Object} Permission flags and role information
 */
export const usePermissions = () => {
  const { user } = useAuth();
  
  const roleID = user?.RoleID;
  const userPermissions = rolePermissions[roleID] || [];
  
  return {
    // Core permission checks
    canRead: userPermissions.includes('read'),
    canWrite: userPermissions.includes('write'),
    canDelete: userPermissions.includes('delete'),
    canAdmin: userPermissions.includes('admin'),
    
    // Role-specific checks
    isAdmin: roleID === 1,
    isManager: roleID === 2,
    isUser: roleID === 3,
    isViewer: roleID === 4,
    
    // Convenience flags
    isReadOnly: roleID === 4,
    canManageUsers: roleID === 1,
    canEditAssets: userPermissions.includes('write'),
    canDeleteAssets: userPermissions.includes('delete'),
    canAddAssets: userPermissions.includes('write'),
    canAddInspections: userPermissions.includes('write'),
    canEditInspections: userPermissions.includes('write'),
    
    // Role info for display
    roleID: roleID,
    roleName: roleNames[roleID] || 'Unknown',
    
    // Helper function to check specific permission
    hasPermission: (permission) => userPermissions.includes(permission),
  };
};

/**
 * Higher-order component to wrap components with permission checking
 * @param {React.Component} WrappedComponent - Component to wrap
 * @param {string} requiredPermission - Permission required to render
 * @param {React.Component} FallbackComponent - Component to render if permission denied
 */
export const withPermission = (WrappedComponent, requiredPermission, FallbackComponent = null) => {
  return function PermissionWrapper(props) {
    const { hasPermission } = usePermissions();
    
    if (!hasPermission(requiredPermission)) {
      return FallbackComponent ? <FallbackComponent {...props} /> : null;
    }
    
    return <WrappedComponent {...props} />;
  };
};

export default usePermissions;
